import { MessageDescriptor } from "react-intl";

import { LinkModalProps } from "@/patterns/modal/src";

//TODO: Rewrite mediaQueries for clarity (i.e tablet v desktop seem too similar)
export const deviceMediaQueries = {
    desktop: `@media (min-width: 1024px)`,
    tablet: `@media (max-width: 1023px)`,
    mobile: `@media (max-width: 768px)`,
    extraSmall: `@media (max-width: 360px)`
};

export const TRACKING_EVENT_NAMES = {
    unitTierBox: "pricing_page_unit_tier_box",
    unitDescriptionTooltipTriggered: "unit-description-tooltip-triggered",
    billingCycleChanged: "billing-cycle-changed",
    planCardCTAButtonClicked: "edition-table-button-clicked",
    unitBreachModalDecision: "unit-breach-modal-decision",
    unitLimitBreachModal: "unit-limit-breach-modal",
    userTierTooltipTriggered: "user-tier-tooltip-triggered",
    showPricingExampleTriggered: "show-pricing-example-triggered",
    hidePricingExampleTriggered: "hide-pricing-example-triggered",
    pricingExampleBillingCycleChanged: "pricing-example-billing-cycle-changed",
    promoCardCTAButtonClicked: "promo-card-button-clicked"
} as const;

export type TrackingEvent = keyof typeof TRACKING_EVENT_NAMES;

export type TrackingEventValues = (typeof TRACKING_EVENT_NAMES)[TrackingEvent];

export const UNIT_TRACKING_TERMS = {
    users: "users",
    agents: "agents"
} as const;

export type CurrencyISOCode = "USD" | "JPY";

export const DEFAULT_COUNTRY_ISO_CODE = "US";
export const DEFAULT_CURRENCY_ISO_CODE = "USD";

export const SpecialPlanNames = {
    FREE: "Free",
    STANDARD: "Standard",
    PREMIUM: "Premium",
    ENTERPRISE: "Enterprise"
} as const;

export type SpecialPlanNames = keyof typeof SpecialPlanNames;

export type ProductPlanNames = (typeof SpecialPlanNames)[SpecialPlanNames];

export const Products = {
    confluence: "confluence",
    jiraSoftware: "jira-software",
    jiraServiceDesk: "jira-servicedesk",
    jiraServiceManagement: "jira-servicedesk"
} as const;

export type Products = typeof Products;
export type ProductKey = keyof Products;
export type ProductValues = Products[ProductKey];

export type ProductOffering<T extends ProductKey> = `${Products[T]}-${Lowercase<
    Exclude<ProductPlanNames, "Enterprise">
>}`;

export type OndemandProductKey<T extends ProductKey> = T extends "confluence"
    ? `pricingplan.${Products[T]}.ondemand`
    : `${Products[T]}.ondemand`;

export type DataCenterProductKey<T extends ProductKey> = T extends "confluence"
    ? `pricingplan.${Products[T]}-data-center`
    : `${Products[T]}.data-center`;

export type OfferingSKU<P extends ProductKey> = ProductOffering<P>;

export type OfferingSKUMap<P extends ProductKey, T> = {
    [key in ProductOffering<P>]: T;
};

export type OfferingHamsMap<P extends ProductKey, T> = {
    [key in OndemandProductKey<P>]: T;
};

export const SpecialProductKeys = {
    JSM: "jira-servicedesk.ondemand",
    BITBUCKET: "com.atlassian.bitbucket.manual"
} as const;

export const DefaultPlanLimits = {
    UNIT_LIMIT_FREE: 10,
    UNIT_LIMIT: 50000,
    UNIT_MIN_ENTERPRISE: 801
} as const;

export type PriceCalculationRequest<P extends ProductKey> = {
    readonly ccpOfferingSKU?: ProductOffering<P>;
    readonly hamsProductKey?: OndemandProductKey<P>;

    readonly numUnits: number;
    readonly period: PricingPeriod;
};

export type PriceCalculationResponse = {
    readonly totalPrice: number;
    readonly avgPerUnitPrice: number;

    readonly breakdown: BreakdownPriceTier[];

    readonly hasFlatRateFirstTier: boolean;
};

export type FormattedPriceCalculationResponse = {
    readonly formattedTotalPrice: string;
    readonly formattedAvgPerUnitPrice: string;

    readonly formattedBreakdown: FormattedBreakdownPriceTier[];
} & PriceCalculationResponse;

export type BreakdownPriceTier = {
    unitsInTier: number;
    unitsAvailableInTier: number;

    unitLimitLower: number;
    unitLimitUpper: number;

    totalPriceForTier: number;
    avgPerUnitPriceForTier: number;
};

export type FormattedBreakdownPriceTier = {
    formattedUnitsInTier: string;
    formattedUnitsAvailableInTier: string;

    formattedUnitLimitLower: string;
    formattedUnitLimitUpper: string;

    formattedTotalPriceForTier: string;
    formattedAvgPerUnitPriceForTier: string;
};

export type PricingPeriod = "MONTHLY" | "ANNUAL";

export type PlanFeatureMessage = {
    featureMessage: LinkModalProps | string;
    lozengeText?: string;
};

export type ProductPlan<T extends ProductKey> = {
    planName: ProductPlanNames;
    offeringSku?: ProductOffering<T>;
    recommendationLimit: number;
    description?: string;
    signupLimit: number;
    signupURLSuffix?: string;
    planFeatureMessages?: PlanFeatureMessage[];
};

//TODO: Where should we hold these values? Used in Product Catalog as well
export type PricingTier = {
    readonly pricingStrategy: PricingStrategyValue;
    readonly unitDescription: string;
    readonly unitLimitLower: number | null;
    readonly unitLimitUpper: number | null;
    readonly prices: ReadonlyArray<PricingTierPrice>;
};

export type PricingTierPrice = {
    readonly currency: "USD" | "JPY";
    readonly unitPrice: number;
};

export type PriceFinderMessages = {
    headerText?: MessageDescriptor; //optional value for Spork Experiment
    subHeaderText?: MessageDescriptor; //optional value for Spork Experiment
    unitCountInputText: MessageDescriptor;
    unitCountInputTooltip: MessageDescriptor;
    monthlyExampleHelpText: MessageDescriptor;
    annualExampleHelpText: MessageDescriptor;
    exampleStandardPlan: MessageDescriptor; // Needs to have a unitCount value
    monthlyExampleUnitCountText: MessageDescriptor;
    monthlyExampleUnitCountAt: MessageDescriptor;
    monthlyExampleUnitCountTotal: MessageDescriptor; // Needs to have a unitCount value
    monthlyExampleEstimatedPrice: MessageDescriptor; // Needs to have a price value
    annualExampleUnitCountTierText: MessageDescriptor;
    annualExampleEstimatedPrice: MessageDescriptor; // Needs to have a price value
    cardOverSignupLimit: MessageDescriptor;
    cardEnterpriseMinimumCount: MessageDescriptor;
    cardUnitCountTierRange: MessageDescriptor; // Needs to have a lowerLimit and upperLimit value
    cardMonthlyPerUnitCount: MessageDescriptor;
    cardFreeDescription: MessageDescriptor;
    freeLimitModalTitle: MessageDescriptor; // Needs to have a freeLimitCount value
    freeLimitModalMessage: MessageDescriptor; // Needs to have a freeLimitCount value
};

export const PricingStrategy = {
    FIXED_COST: "FIXED_COST",
    MARGINAL_PER_UNIT: "MARGINAL_PER_UNIT",
    PER_UNIT: "PER_UNIT"
} as const;

export type PricingStrategyValue =
    (typeof PricingStrategy)[keyof typeof PricingStrategy];

export function isPricingStrategy(value: PricingStrategyValue) {
    switch (value) {
        case PricingStrategy.FIXED_COST:
        case PricingStrategy.MARGINAL_PER_UNIT:
        case PricingStrategy.PER_UNIT:
            return true;
        default:
            return false;
    }
}

export function parsePricingStrategy(
    value: PricingStrategyValue
): PricingStrategyValue {
    if (isPricingStrategy(value)) {
        return value;
    } else {
        throw new SyntaxError(`${value} is not a valid PricingStrategy`);
    }
}

export type PricingPlan = ReadonlyArray<PricingTier>;

export type PricingPlans = {
    readonly monthly?: PricingPlan;
    readonly annual?: PricingPlan;
};
